import { useCallback } from "react";

interface UseVoiceChatParams {
  isMuted: boolean;
  setIsMuted: (muted: boolean) => void;
  isConnected: boolean;
  isRecording: boolean;
  audioStreamRef: React.MutableRefObject<MediaStream | null>;
  startAudioRecording: () => Promise<void>;
  stopAudioRecording: (
    shouldStopTracks?: boolean,
    options?: {
      finalizeTurn?: boolean;
      reason?: string;
      trailingSilenceMs?: number;
    },
  ) => Promise<void>;
  cancelTrailingSilence?: () => void;
  initializeSession: () => Promise<void>;
  isMicDisabledByWarning: boolean;
  chunkQueue: React.MutableRefObject<Int16Array[]>;
  isCurrentlySendingRef: React.MutableRefObject<boolean>;
  silenceTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>;
}

export const useVoiceChat = ({
  isMuted,
  setIsMuted,
  isConnected,
  isRecording,
  audioStreamRef,
  startAudioRecording,
  stopAudioRecording,
  cancelTrailingSilence,
  initializeSession,
  isMicDisabledByWarning,
  chunkQueue,
  isCurrentlySendingRef,
  silenceTimeoutRef,
}: UseVoiceChatParams) => {
  const startVoiceChat = useCallback(
    async (isInputAudioMuted?: boolean) => {
      if (!isConnected) {
        await initializeSession();
      }

      if (audioStreamRef.current) {
        if (isInputAudioMuted) {
          audioStreamRef.current.getAudioTracks().forEach((track) => {
            track.enabled = false;
          });
          setIsMuted(true);
        } else {
          audioStreamRef.current.getAudioTracks().forEach((track) => {
            track.enabled = true;
          });
          setIsMuted(false);
          if (!isRecording) {
            await startAudioRecording();
          }
        }
      } else {
        setIsMuted(!!isInputAudioMuted);
        await initializeSession();
      }
    },
    [
      isConnected,
      audioStreamRef,
      initializeSession,
      isRecording,
      startAudioRecording,
      setIsMuted,
    ],
  );

  const stopVoiceChat = useCallback(async () => {
    await stopAudioRecording(true);
    setIsMuted(true);
  }, [stopAudioRecording, setIsMuted]);

  const muteInputAudio = useCallback(
    (options?: { trailingSilenceMs?: number }) => {
      if (isMicDisabledByWarning) return;

      const currentStream = audioStreamRef.current;
      if (currentStream) {
        currentStream.getAudioTracks().forEach((track) => {
          track.enabled = false;
        });
      }
      setIsMuted(true);
      stopAudioRecording(false, {
        finalizeTurn: true,
        reason: "mute",
        trailingSilenceMs: options?.trailingSilenceMs,
      });

      if (!options?.trailingSilenceMs) {
        chunkQueue.current = [];
        isCurrentlySendingRef.current = false;
      }
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = null;
      }
    },
    [
      isMicDisabledByWarning,
      audioStreamRef,
      setIsMuted,
      stopAudioRecording,
      chunkQueue,
      isCurrentlySendingRef,
      silenceTimeoutRef,
    ],
  );

  const unmuteInputAudio = useCallback(() => {
    if (isMicDisabledByWarning) return;

    cancelTrailingSilence?.();

    const currentStream = audioStreamRef.current;

    if (currentStream) {
      currentStream.getAudioTracks().forEach((track) => {
        track.enabled = true;
      });
      setIsMuted(false);
      chunkQueue.current = [];
      isCurrentlySendingRef.current = false;
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = null;
      }
      if (!isRecording) {
        startAudioRecording();
      }
    } else {
      setIsMuted(false);
      initializeSession();
    }
  }, [
    isMicDisabledByWarning,
    audioStreamRef,
    setIsMuted,
    isRecording,
    startAudioRecording,
    initializeSession,
    chunkQueue,
    isCurrentlySendingRef,
    silenceTimeoutRef,
    cancelTrailingSilence,
  ]);

  return {
    startVoiceChat,
    stopVoiceChat,
    muteInputAudio,
    unmuteInputAudio,
    isMuted,
    isVoiceChatActive: isConnected && !isMuted,
    isVoiceChatLoading: false,
  };
};
